import { createContext, FC, useContext, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useDeepCompareEffect, useDeepCompareMemo } from "use-deep-compare";
import * as _ from "lodash";
import moment from "moment";
import { AjaxError } from "rxjs/ajax";

import {
  ChartData,
  ChartDataSourceType,
  ChartEntity,
  ChartFilters,
  ChartFilterType,
  Charts,
  ChartTimePeriod,
  ChartType,
  DataDescriptorEntity,
  Enterprise,
  EnterpriseAppState,
  UnitSystemConversionMap
} from "@ctra/api";

import { isDispatched, isPending, isRejected, Nullable, TS, UnitSystem } from "@ctra/utils";

import { useLocalization } from "@base";

import { useDebounce, useKPISupport, useViews } from "../../hooks";
import { useChartFilters } from "../ChartFilterContext";
import { ChartAPIProviderProps, ContextType } from "./typings";

/**
 * Default Chart API Context
 */
const DefaultContext = createContext<ContextType>({
  charts: [
    {
      entity: "Empty context: no chart" as unknown as ChartEntity,
      meta: undefined,
      data: undefined
    }
  ],
  metricVariants: [],
  api: {
    fetchData: _.noop,
    changeView: _.noop,
    addToOverlay: _.noop,
    removeFromOverlay: _.noop
  },
  meta: {
    requestURL: [],
    unitSystem: UnitSystemConversionMap[UnitSystem.metric],
    view: ChartType.basicLine,
    supportedViews: {} as ContextType["meta"]["supportedViews"],
    farms: { hasNoSupport: [], hasSupport: [] },
    sourceType: ChartDataSourceType.dataPoints,
    isLoading: true,
    hasFailed: false,
    hasConsent: false
  }
});

/**
 * Adjust the date range to the current date if the end date is in the future
 * @param {string} startDate
 * @param {string} endDate
 * @param {Nullable<string>} maxProjection
 * @param {boolean} forceProjection
 * @returns {ChartTimePeriod}
 */
const adjustTimePeriod = (
  { startDate, endDate }: ChartTimePeriod,
  maxProjection?: Nullable<string>,
  forceProjection?: boolean
): ChartTimePeriod => {
  const endMoment = TS.asMoment(endDate);
  const now = moment().utc().endOf("day");
  const kindOfToday = now.diff(endMoment, "days") < 2;
  let adjustedEndDate = endDate;

  if (kindOfToday && forceProjection) {
    adjustedEndDate = endMoment.clone().add(moment.duration(maxProjection)).toISOString();
  }

  if (endMoment.isAfter(now) && maxProjection) {
    const maxEndDate = endMoment.clone().add(moment.duration(maxProjection));
    adjustedEndDate = (maxEndDate.isAfter(endMoment) ? endMoment : maxEndDate).toISOString();
  }

  return { startDate, endDate: adjustedEndDate };
};

/**
 * Provide an API for displaying/updating charts and set meta data used to initialize filter context values
 * @param {string | Array<DataDescriptorEntity["id"]>} metricVariantID
 * @param {number | undefined} farmID
 * @param {ChartType | undefined} defaultView
 * @param {React.ReactElement<any, string | React.JSXElementConstructor<any>> | string | number | {} | Iterable<React.ReactNode> | React.ReactPortal | boolean | null | undefined} children
 * @returns {JSX.Element}
 */
const _ChartAPIProvider: FC<ChartAPIProviderProps> = ({ metricVariantID, farmID, defaultView, children }) => {
  const dispatch = useDispatch();
  const { debounce } = useDebounce();
  const { unitSystem: contextUnitSystem } = useLocalization();

  const {
    filters,
    timePeriod,
    bucketSize,
    api: { getMetadata, setMetadata }
  } = useChartFilters();

  /**
   * Convert the prop to a list of data descriptor ids
   * @todo find how `metricVariantID` can be undefined!!!
   * @type {DataDescriptorEntity["id"][]}
   */
  const initialMetricVariantIDList: DataDescriptorEntity["id"][] = _.isString(metricVariantID)
    ? [metricVariantID]
    : _.isArray(metricVariantID)
    ? metricVariantID
    : [];

  const [metricVariantIDList, setMetricVariantIDList] =
    useState<DataDescriptorEntity["id"][]>(initialMetricVariantIDList);

  /**
   * Update the metric variant id list when the prop changes
   */
  useDeepCompareEffect(() => {
    setMetricVariantIDList(initialMetricVariantIDList);
  }, [initialMetricVariantIDList]);

  /**
   * Get the data descriptors
   * @type {DataDescriptorEntity[]}
   */
  const metricVariants: DataDescriptorEntity[] = useSelector<EnterpriseAppState, DataDescriptorEntity[]>(
    (state) => Enterprise.entities.getDataDescriptor(state, { id: metricVariantIDList })
  );

  /**
   * Make a map of variants by ID
   * @type {Dictionary<DataDescriptorEntity>}
   */
  const variantMap = _.keyBy(metricVariants, "id");

  /**
   * Resolve the chart objects and a basic chart switching API using the data descriptors
   * @note the return value based on the metric variant id list (keeping the [main, ...overlays] order)
   *       and only contains views which are supported by every chart
   * @example
   * useViews(["5f4d29db0726402480022ffc", "6102f60b4b4bb695e7ec7250"]) => {
   *   views: ["basicLine", "basicBar"],
   *   currentView: "basicLine",
   *   setCurrentView: (view) => { ... },
   *   charts: [ChartEntity, ChartEntity]
   * }
   */
  const {
    views: supportedViews,
    currentView,
    setCurrentView,
    charts
  } = useViews(metricVariantIDList, defaultView);

  const [mainChartEntity] = charts;

  /**
   * Farm support information, partitions the farm id list based on chart support
   * @note this is important so when unsupported farms try to access the chart, we can display an error message
   * @note in case of overlays, the farm support list is created via intersection
   */
  const { hasSupport, hasNoSupport } = useKPISupport(metricVariantIDList, farmID ? [farmID] : undefined);

  /**
   * Adjust the time period using the max projection interval of the main chart
   * @type {ChartTimePeriod}
   */
  const adjustedTimePeriod: ChartTimePeriod = adjustTimePeriod(
    timePeriod,
    mainChartEntity.dataProperties.maxProjectionInterval
  );

  /**
   * Set up query filters for the chart if the filters need to be passed to the backend
   * @type {ChartFilters | undefined}
   */
  const queryFilters = useDeepCompareMemo(() => {
    return _.reduce(
      metricVariantIDList,
      (acc, id) => {
        const filtersOfVariant = _.get(filters, [id], {});
        const { filterDefinitions } = getMetadata(id);
        const siteIDFilter = _.find(filterDefinitions, (def) => def.name === "siteId");

        if (siteIDFilter && !siteIDFilter.isBackendAggregated) {
          console.error("Site ID filter is not backend aggregated for", id);
        }

        /**
         * Make a list of filter names which need to be passed to the backend
         * @type {ChartFilterType[]}
         */
        const backendAggregatedDefinitions = _.map(
          _.filter(filterDefinitions, "isBackendAggregated"),
          "name"
        );

        acc[id] = _.pick(
          filtersOfVariant,
          siteIDFilter ? [...backendAggregatedDefinitions, siteIDFilter.name] : backendAggregatedDefinitions
        );

        return acc;
      },
      {} as Record<DataDescriptorEntity["id"], ChartFilters>
    );
  }, [metricVariantIDList, filters]);

  /**
   * Always use the context unit system as the default unit system, even if imperial is not supported
   * @type {UnitSystemParam}
   */
  const unitSystem = UnitSystemConversionMap[contextUnitSystem];

  /**
   * Make a hash list from the chart fingerprints, this will be unique across the app
   * @type {string[]}
   */
  const hashList: string[] = _.map(charts, ({ id }, idx) => {
    const variantID = metricVariantIDList[idx];
    const queryFilter = queryFilters[variantID];

    return Charts.utils.makeChartHash(
      id,
      adjustedTimePeriod,
      bucketSize,
      hasSupport,
      unitSystem,
      queryFilter
    );
  });

  /**
   * Check if there is a URL in our store already
   * @type {Array<ChartData>}
   */
  const data: Array<ChartData> = useSelector<EnterpriseAppState, Array<ChartData>>((state) =>
    _.isEmpty(adjustedTimePeriod) ? [] : Enterprise.entities.getChartData(state, { hash: hashList })
  );

  /**
   * Tell whether the fetching action has already been dispatched
   * @type {boolean}
   */
  const dispatched: boolean = useSelector<EnterpriseAppState, boolean>((state) =>
    _.every(hashList, (hash) => isDispatched(state, Charts.types.FETCH_CHART_DATA, { primaryValue: hash }))
  );

  /**
   * Tell whether the chart data is being fetched
   * @type {boolean}
   */
  const isLoading: boolean = useSelector<EnterpriseAppState, boolean>((state) =>
    _.some(hashList, (hash) => isPending(state, Charts.types.FETCH_CHART_DATA, { primaryValue: hash }))
  );

  /**
   * Tell whether the chart has failed loading
   * @type {boolean}
   */
  const hasFailed: boolean = useSelector<EnterpriseAppState, boolean>((state) =>
    _.some(hashList, (hash) => isRejected(state, Charts.types.FETCH_CHART_DATA, { primaryValue: hash }))
  );

  /**
   * Get the request URL for the chart
   * @type {Nullable<string>}
   */
  const requestURL = useSelector<EnterpriseAppState, Array<Nullable<string>>>((state) =>
    _.map(hashList, (hash) => Enterprise.entities.getChartRequestURL(state, { hash }))
  );

  /**
   * Get ajax error if the chart has failed to load
   */
  const error = useSelector<EnterpriseAppState, AjaxError[]>((state) =>
    _.map(hashList, (hash) => Enterprise.entities.getChartError(state, { hash }))
  );

  /**
   * If the error code is 401
   * then the farm owner does not have consent
   * to view the particular chart/KPI
   */
  // @ts-expect-error - error is set to NULL
  const hasConsent = error?.status !== 401;

  /**
   * Manage the metadata and update the filtering context when the charts change
   * @note this mostly happens when the user switches between charts, and we replace it without remounting
   */
  useDeepCompareEffect(() => {
    _.map(charts, (entity, idx) => {
      const metricVariantID = metricVariantIDList[idx];
      const metadata = getMetadata(metricVariantID);
      const isBackendAggregated = _.some(entity.filters, "isBackendAggregated");
      const { filters: filterDefinitions } = entity;

      /**
       * Set the metadata for the chart
       */
      setMetadata(metricVariantID, {
        ...metadata,
        filterDefinitions,
        flags: { ...metadata.flags, isBackendAggregated },
        supportedfarmIDs: hasSupport
      });
    });
  }, [charts, hasSupport]);

  /**
   * Fetch chart data, only for external use
   */
  const fetchData = () => {
    _.forEach(charts, (entity, idx) => {
      const metricVariantID = metricVariantIDList[idx];
      const queryFilter = queryFilters[metricVariantID];

      const {
        valueProperties: { zeroRenderingInterval }
      } = variantMap[metricVariantID];

      dispatch(
        Charts.actions.fetchChartData.start(
          entity.id,
          ChartDataSourceType.dataPoints,
          adjustedTimePeriod,
          bucketSize,
          hasSupport,
          unitSystem,
          queryFilter,
          entity.dataURL,
          { zeroRenderingInterval }
        )
      );
    });
  };

  /**
   * Fetch data for each chart in the bundle
   */
  useDeepCompareEffect(() => {
    const hasTimePeriod = !_.isEmpty(adjustedTimePeriod);
    const hasData = _.size(data) === _.size(metricVariantIDList) && _.every(data);

    /**
     * @todo allow re-dispatch when the chart has failed the user requests a reload
     */
    if (hasTimePeriod && !hasData && !dispatched) {
      debounce(
        () => {
          _.forEach(charts, (entity, idx) => {
            const metricVariantID = metricVariantIDList[idx];
            const queryFilter = queryFilters[metricVariantID];

            const zeroRenderingInterval = _.get(
              variantMap,
              [metricVariantID, "valueProperties", "zeroRenderingInterval"],
              null
            );

            dispatch(
              Charts.actions.fetchChartData.start(
                entity.id,
                ChartDataSourceType.dataPoints,
                adjustedTimePeriod,
                bucketSize,
                hasSupport,
                unitSystem,
                queryFilter,
                entity.dataURL,
                { zeroRenderingInterval }
              )
            );
          });
        },
        {
          chartID: _.map(charts, "id").join(","),
          adjustedTimePeriod,
          bucketSize,
          hasSupport,
          unitSystem,
          queryFilters
        }
      );
    }
  }, [
    dispatched,
    data,
    charts,
    hasSupport,
    adjustedTimePeriod,
    unitSystem,
    bucketSize,
    queryFilters,
    metricVariantIDList
  ]);

  /**
   * API interface
   * @type {{changeView: (value: (((prevState: ChartType) => ChartType) | ChartType)) => void, addToOverlay: (metricVariantID: DataDescriptorEntity["id"]) => void, removeFromOverlay: (metricVariantID: DataDescriptorEntity["id"]) => void}}
   */
  const api: ContextType["api"] = {
    fetchData,
    changeView: setCurrentView,
    addToOverlay: (metricVariantID: DataDescriptorEntity["id"]) => {
      return setMetricVariantIDList((prev) => _.uniq([...prev, metricVariantID]));
    },
    removeFromOverlay: (metricVariantID: DataDescriptorEntity["id"]) => {
      return setMetricVariantIDList((prev) => _.filter(prev, (id) => id !== metricVariantID));
    }
  };

  /**
   * Context meta
   * @type {{requestURL: string, supportedViews: Record<ChartType, string>, unitSystem: UnitSystemParam, farms: {hasSupport: Array<FarmEntity["id"]>, hasNoSupport: Array<FarmEntity["id"]>}, sourceType: ChartDataSourceType}}
   */
  const meta: ContextType["meta"] = {
    requestURL,
    view: currentView,
    supportedViews,
    unitSystem,
    isLoading: !dispatched || isLoading,
    hasFailed,
    hasConsent,
    farms: {
      hasSupport,
      hasNoSupport
    },
    sourceType: ChartDataSourceType.dataPoints
  };

  return (
    <DefaultContext.Provider
      value={{
        metricVariants,
        charts: _.map(charts, (entity, idx) => ({
          entity,
          data: _.get(data, [idx, "data"]),
          meta: _.get(data, [idx, "meta"])
        })),
        api,
        meta
      }}
    >
      {children}
    </DefaultContext.Provider>
  );
};

export const ChartAPIContext = {
  Provider: _ChartAPIProvider,
  Consumer: DefaultContext.Consumer
};

/**
 * Hook for Chart context
 * @returns
 */
export const useChartAPI = (): ContextType => useContext<ContextType>(DefaultContext);
